import { useTranslation } from 'next-i18next'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'

import AuthUtil from '@src/quince-site-common/src/utils/authUtil'
import { setJoinWaitlistPhoneNumber } from '@src/quince-site-common/src/utils/join-waitlist-phone-storage'
import {
  clearWaitlistSmsModalSuppression,
  recordWaitlistSmsModalDismiss,
} from '@src/quince-site-common/src/utils/waitlist-sms-suppression'
import { validatePhone } from '@src/quince-site-integrated/src/utils/validation'

import Button, { BUTTON_SIZE, BUTTON_TYPES } from '../button'
import Modal from '../modal'

import {
  WAITLIST_SMS_PHONE_PLACEHOLDER,
  WAITLIST_SMS_VENDOR_PRIVACY_URL,
  WAITLIST_SMS_VENDOR_TERMS_URL,
} from './waitlist-sms-modal.constants'
import styles from './waitlist-sms-modal.module.scss'

interface WaitlistSmsModalProps {
  open: boolean
  onClose: () => void
  onSubmitPhone?: (phoneNumber: string) => void
}

const formatPhoneNumber = (rawDigits: string): string => {
  const digits = rawDigits.replace(/\D/g, '').slice(0, 10)
  if (digits.length === 0) return ''
  if (digits.length < 4) return `(${digits}`
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
}

const extractDigits = (value: string): string => value.replace(/\D/g, '')

const AUTO_CLOSE_AFTER_MS = 20 * 60 * 1000

const RESIZE_TOTAL_MS = 460

type Phase = 'capture' | 'resizing' | 'success'

const WaitlistSmsModal = ({ open, onClose, onSubmitPhone }: WaitlistSmsModalProps) => {
  const { t } = useTranslation('common')
  const [phone, setPhone] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [phase, setPhase] = useState<Phase>('capture')

  const captureRef = useRef<HTMLDivElement>(null)
  const successRef = useRef<HTMLDivElement>(null)
  const phaseRef = useRef<Phase>('capture')
  const [heights, setHeights] = useState<{ capture: number; success: number } | null>(null)

  const isSuccess = phase === 'success'

  useEffect(() => {
    phaseRef.current = phase
  }, [phase])

  const resetModalState = () => {
    setPhone('')
    setErrorMessage('')
    setPhase('capture')
    setHeights(null)
  }

  const handleClose = () => {
    if (isSuccess) {
      clearWaitlistSmsModalSuppression()
    } else {
      recordWaitlistSmsModalDismiss()
    }
    resetModalState()
    onClose()
  }

  const handleAutoClose = () => {
    if (phaseRef.current !== 'success') {
      recordWaitlistSmsModalDismiss()
    }
    resetModalState()
    onClose()
  }

  useEffect(() => {
    if (!open) return
    const timerId = window.setTimeout(handleAutoClose, AUTO_CLOSE_AFTER_MS)
    return () => {
      window.clearTimeout(timerId)
    }
  }, [open])

  useLayoutEffect(() => {
    if (!open) return

    const measure = () => {
      const capture = captureRef.current?.offsetHeight
      const success = successRef.current?.offsetHeight
      setHeights(prev => ({
        capture: capture && capture > 0 ? capture : (prev?.capture ?? 0),
        success: success && success > 0 ? success : (prev?.success ?? 0),
      }))
    }

    measure()

    const observer = new ResizeObserver(measure)
    if (captureRef.current) observer.observe(captureRef.current)
    if (successRef.current) observer.observe(successRef.current)
    return () => {
      observer.disconnect()
    }
  }, [open])

  useEffect(() => {
    if (phase !== 'resizing') return
    const timerId = window.setTimeout(() => {
      setPhase('success')
    }, RESIZE_TOTAL_MS)
    return () => {
      window.clearTimeout(timerId)
    }
  }, [phase])

  const handleSubmit = () => {
    if (!validatePhone(phone)) {
      setErrorMessage(t('valid_phone_number_required'))
      return
    }
    const digits = extractDigits(phone)
    setErrorMessage('')
    if (AuthUtil.isLoggedIn()) {
      setJoinWaitlistPhoneNumber(digits)
    }
    onSubmitPhone?.(digits)
    setPhase('resizing')
  }

  const wrapperHeight = heights ? (phase === 'capture' ? heights.capture : heights.success) : undefined

  return (
    <Modal
      open={open}
      onClose={handleClose}
      showCloseButton={true}
      className={styles.modal}
      closeButtonClassName={styles.closeButton}
      responsiveBottomSheet={true}
    >
      <div
        className={`${styles.transitionWrapper} ${phase === 'resizing' ? styles.transitionWrapperAnimating : ''}`}
        style={wrapperHeight ? { height: wrapperHeight } : undefined}
      >
        <div
          ref={captureRef}
          className={`${styles.captureContainer} ${phase !== 'capture' ? styles.contentHidden : ''}`}
          aria-hidden={phase !== 'capture'}
        >
          <div className={styles.heading}>{t('waitlist_sms_modal_heading')}</div>
          <div className={styles.divider} />
          <div className={styles.subheading}>{t('waitlist_sms_modal_subheading')}</div>
          <input
            type="tel"
            inputMode="tel"
            className={styles.phoneInput}
            placeholder={WAITLIST_SMS_PHONE_PLACEHOLDER}
            value={phone}
            onChange={e => {
              setPhone(formatPhoneNumber(e.target.value))
              if (errorMessage) setErrorMessage('')
            }}
            aria-label={WAITLIST_SMS_PHONE_PLACEHOLDER}
          />
          {errorMessage && <div className={styles.errorMessage}>{errorMessage}</div>}
          <div className={styles.consent}>
            {t('waitlist_sms_modal_consent_body')}
            {t('waitlist_sms_modal_consent_legal_prefix')}
            <a
              className={styles.consentLink}
              href={WAITLIST_SMS_VENDOR_TERMS_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('waitlist_sms_modal_terms_link_label')}
            </a>
            {t('waitlist_sms_modal_consent_legal_separator')}
            <a
              className={styles.consentLink}
              href={WAITLIST_SMS_VENDOR_PRIVACY_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('privacy_policy')}
            </a>
            {t('waitlist_sms_modal_consent_legal_suffix')}
          </div>
          <Button
            className={styles.submitButton}
            type={BUTTON_TYPES.PRIMARY}
            size={BUTTON_SIZE.LARGE}
            round={true}
            onClick={handleSubmit}
          >
            {t('waitlist_sms_modal_notify_me')}
          </Button>
        </div>
        <div
          ref={successRef}
          className={`${styles.successContainer} ${phase === 'success' ? styles.fadeIn : styles.successPending}`}
          aria-hidden={phase !== 'success'}
        >
          <div className={styles.successHeading}>{t('waitlist_sms_modal_success_heading')}</div>
          <div className={styles.successBody}>{t('waitlist_sms_modal_success_body')}</div>
          <Button
            className={styles.submitButton}
            type={BUTTON_TYPES.PRIMARY}
            size={BUTTON_SIZE.LARGE}
            round={true}
            onClick={handleClose}
          >
            {t('continue_shopping')}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default WaitlistSmsModal
